import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, LockKey } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset! Please log in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-up rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
        <Link to="/" className="inline-block">
          <p className="font-heading text-3xl font-extrabold tracking-tight text-primary">Avinya <span className="font-semibold text-stone-800">Bookings</span></p>
          <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.32em] text-amber-600">Book · Relax · Celebrate</p>
        </Link>
        <h1 className="mt-6 flex items-center gap-2 font-heading text-2xl font-semibold text-stone-900"><LockKey size={22} className="text-primary" /> Set a new password</h1>
        {!token ? (
          <p className="mt-3 text-sm text-destructive" data-testid="reset-no-token">This reset link is invalid or incomplete. Please request a new one from the <Link to="/forgot-password" className="font-semibold underline">Forgot password</Link> page.</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label>New password</Label>
              <div className="relative mt-1">
                <Input data-testid="reset-new" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-xl pr-10" placeholder="At least 8 characters" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">{show ? <EyeSlash size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            <div><Label>Confirm new password</Label><Input data-testid="reset-confirm" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-xl" placeholder="Re-enter new password" /></div>
            {error && <p data-testid="reset-error" className="text-sm text-destructive">{error}</p>}
            <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full rounded-xl bg-primary py-6 text-base hover:bg-green-900">{loading ? "Resetting…" : "Reset Password"}</Button>
          </form>
        )}
        <p className="mt-6 text-center text-sm text-stone-500"><Link to="/login" className="font-semibold text-primary hover:underline">Back to Login</Link></p>
      </div>
    </div>
  );
}
